/**
 * PageLoader — espera de chunk/sessão.
 *
 * Vivia dentro do `AppRoutes` até 06/08. Saiu pra cá porque o
 * `PublicOrGuarded` também precisa dele e importar do `AppRoutes` faria ciclo
 * (o AppRoutes importa o guard).
 *
 * 17/09/2026 (rodada de performance) — dois ajustes medidos:
 *
 *  1. **Aparece com atraso.** Com a pré-carga de chunks (`routePrefetch.ts`) a
 *     maioria das trocas de rota assenta em menos de 100 ms, e um loader que
 *     pinta no primeiro frame vira um PISCAR entre duas telas prontas — o que
 *     lia como "o app engasgou". Só quem espera mais de 160 ms vê o loader,
 *     e ele entra em fade, não em corte.
 *  2. **É a marca, não um disco.** O disco girando era o único desenho do app
 *     que não era do Livra — o wordmark ocupa o lugar dele.
 *
 * ⚠️ Não é um ícone separado por cima do wordmark: duas tentativas nesse
 * sentido (`docs/rounds/2026-09.md`, 17/09) leram mal rodando de verdade —
 * a segunda delas era redundante bem aqui, um "L" sozinho sobre o "Livra"
 * escrito embaixo. O wordmark sozinho é a marca inteira.
 *
 * Estilo inline de propósito: é o que aparece ANTES de qualquer chunk de CSS
 * do app carregar, então não pode depender de classe do design system.
 */
import { useEffect, useState } from "react";
import { useWideDesktopFrame } from "../lib/layout/desktopShell";
import { LivraLogo } from "../components/brand/LivraLogo";

/** Abaixo disto a espera é imperceptível e o loader só atrapalharia. */
export const PAGE_LOADER_DELAY_MS = 160;

/**
 * `height`: dentro da casca de desktop o loader ocupa o frame (`100%`), não a
 * janela — `100dvh` passaria da altura do frame (que desconta o topo) e
 * abriria uma rolagem à toa.
 */
export function PageLoader({ height = "100dvh" }: { height?: string } = {}) {
  // Na casca de desktop a espera ocupa a área larga, não a coluna de 430px
  // (05/10, medido): quando a página suspende, o React desliga os efeitos da
  // tela de boot que segurava o frame largo, e sem este pedido a coluna
  // voltava por 300–700 ms entre o boot e a página. Fora da casca, nada.
  useWideDesktopFrame();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), PAGE_LOADER_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Carregando"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height,
        width: "100%",
        opacity: visible ? 1 : 0,
        transition: "opacity 220ms ease-out",
      }}
    >
      <LivraLogo height={30} style={{ color: "var(--text-primary, #2a2521)" }} />
    </div>
  );
}
